import { zodResolver } from "@hookform/resolvers/zod";
import { FunctionComponent } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { NavLink, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import FormContainer from "../../components/FormContainer";
import { useSendEmailOtpMutation, useVerifyEmailOtpMutation } from "../../redux/auth.api";
import { authSelector, backTo2FA } from "../../redux/auth.slice";
import { EmailOtpSchema } from "./schema/emailOtpSchema";

export const EmailOtpPage: FunctionComponent = () => {
	const { t, ready } = useTranslation();

	const ts = (key: string) => t(`emailOtp.${key}`);

	const { pendingEmailOtpUserId } = useAppSelector(authSelector);

	const [verify, { isLoading, error: verifyError }] = useVerifyEmailOtpMutation();
	const [resend, { isLoading: isResending }] = useSendEmailOtpMutation();

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm({
		defaultValues: { code: "" },
		resolver: zodResolver(EmailOtpSchema),
	});

	const onSubmit = async ({ code }: { code: string }) => {
		try {
			await verify({ userId: pendingEmailOtpUserId!, otp: code }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const onResend = async () => {
		try {
			await resend({ userId: pendingEmailOtpUserId! }).unwrap();
		} catch (err) {
			console.error(err);
		}
	};

	const errorMessage =
		errors?.code?.message ||
		(verifyError as FetchBaseQueryError & { data?: { message: string } })?.data?.message;

	const dispatch = useAppDispatch();

	if (!ready) {
		return null;
	}

	return (
		<div className="w-full h-full flex items-center justify-center">
			<FormContainer
				description={ts("description")}
				formFooter={
					<div className="w-full flex justify-between items-center">
						<NavLink
							className="bb-link text-[14px] text-nowrap"
							to="#"
							onClick={(e) => {
								e.preventDefault();
								dispatch(backTo2FA());
							}}
						>
							{"Back to 2FA"}
						</NavLink>
						<NavLink
							className="bb-link text-[14px] text-nowrap"
							to="#"
							onClick={(e) => {
								e.preventDefault();
								onResend();
							}}
						>
							{isResending ? ts("resending") : ts("resend")}
						</NavLink>
					</div>
				}
				handleSubmit={handleSubmit(onSubmit)}
				isLoading={isLoading}
				submitButtonText={ts("cta")}
				title={ts("title")}
			>
				<Controller
					control={control}
					name="code"
					render={({ field }) => (
						<FormTextField
							{...field}
							autoComplete="off"
							errorMessage={errorMessage}
							label={ts("code")}
							placeHolder={ts("codePlaceholder")}
						/>
					)}
				/>
			</FormContainer>
		</div>
	);
};
